import React, { useRef, useEffect } from 'react';
import styled from 'styled-components';
import _size from 'lodash/size';
import media from 'styles/media';
import { Div } from 'styles/elements/div';
import { DateInputShort, YearInput } from './inputs';
import { LabelledInput as LabelledDateInput } from './labelledInput';

const InnerContainer = styled(Div)`
  flex: 0.5 0.5 auto;
  ${media.extraSmallAndBelow`
    flex: 1 1 auto;
  `}
`;

export const ConstructedDateInput = props => {
  const {
    day,
    month,
    year,
    onUpdateDate,
    isInvalid,
    inputRef,
    minYear,
    maxYear,
    isReadOnly,
  } = props;
  const monthRef = useRef();
  const yearRef = useRef();
  const handleChange = (value, valueType, maxLength) => {
    if (_size(value) <= maxLength) {
      onUpdateDate(value, valueType);
    }
  };
  const handleFocus = (event, nextElementToFocus, maxLength) => {
    // event.persist prevents react from reclaiming the event to allow event.target to remain on the current input, see: https://reactjs.org/docs/legacy-event-pooling.html
    event.persist();
    // only allow numeric characters, tab, backspace or arrow keys
    const allowInput = /^\d+$/.test(event.key) || event.key === 'Backspace' || event.key === 'Tab' || /^Arrow.*$/.test(event.key);
    if (!allowInput) {
      event.preventDefault();
    }
    const valueLength = _size(event.target.value) + 1;
    const isNumericInput = !Number.isNaN(Number(event.key));
    if (valueLength >= maxLength && nextElementToFocus && isNumericInput) {
      // setTimeout adds focus() to end of JS "Job Queue" so happens after all events
      setTimeout(() => {
        if (_size(event.target.value) >= maxLength) {
          nextElementToFocus.current.focus();
        }
      }, 0);
    }
  };
  useEffect(() => {
    if (isInvalid) {
      inputRef.current.focus();
    }
  }, [isInvalid, inputRef]);
  return (
    <Div>
      <InnerContainer>
        <LabelledDateInput label="Day">
          <DateInputShort
            readOnly={isReadOnly}
            type="number"
            ref={inputRef}
            placeholder="DD"
            value={day}
            onKeyDown={event => handleFocus(event, monthRef, 2)}
            onChange={e => handleChange(e.target.value, 'DAY', 2)}
            min="1"
            max="31"
            isInvalid={isInvalid}
            aria-label="Day"
            id="dayDate-input"
            data-automation="dayDate-input"
          />
        </LabelledDateInput>
        <LabelledDateInput label="Month">
          <DateInputShort
            readOnly={isReadOnly}
            type="number"
            ref={monthRef}
            placeholder="MM"
            value={month}
            onKeyDown={event => handleFocus(event, yearRef, 2)}
            onChange={e => handleChange(e.target.value, 'MONTH', 2)}
            min="1"
            max="12"
            isInvalid={isInvalid}
            aria-label="Month"
            id="monthDate-input"
            data-automation="monthDate-input"
          />
        </LabelledDateInput>
        <LabelledDateInput label="Year">
          <YearInput
            readOnly={isReadOnly}
            type="number"
            ref={yearRef}
            placeholder="YYYY"
            value={year}
            onKeyDown={event => handleFocus(event, null, 4)}
            onChange={e => handleChange(e.target.value, 'YEAR', 4)}
            min={minYear}
            max={maxYear}
            isInvalid={isInvalid}
            aria-label="Year"
            id="yearDate-input"
            data-automation="yearDate-input"
          />
        </LabelledDateInput>
      </InnerContainer>
    </Div>
  );
};

const DateInput = Div;
DateInput.LabelledDateInput = LabelledDateInput;
DateInput.InnerContainer = InnerContainer;
DateInput.YearInput = YearInput;
DateInput.DateInputShort = DateInputShort;

export default DateInput;
